/**
 * Gateway WebSocket client for LexPortal.
 *
 * Simplified reimplementation of lexruntime/openclaw/ui/src/ui/gateway.ts, stripping
 * out device-identity crypto (not needed for a monitoring dashboard).
 * Supports token auth (Lexiun JWT or simple shared token) and password auth
 * via the gateway's challenge-response connect handshake.
 */

import type { GatewayConnectionState } from "@/types/metrics";

// ── Wire frames ─────────────────────────────────────────────────

export type GatewayEventFrame = {
  type: "event";
  event: string;
  payload?: unknown;
  seq?: number;
};

export type GatewayResponseFrame = {
  type: "res";
  id: string;
  ok: boolean;
  payload?: unknown;
  error?: { code: string; message: string; details?: unknown };
};

export type GatewayHelloOk = {
  type: "hello-ok";
  protocol: number;
  server: { version: string; commit?: string; host?: string; connId: string };
  features: { methods: string[]; events: string[] };
  snapshot?: unknown;
  auth?: { deviceToken?: string; role?: string; scopes?: string[]; issuedAtMs?: number };
  policy?: { maxPayload?: number; maxBufferedBytes?: number; tickIntervalMs?: number };
};

// ── Options ─────────────────────────────────────────────────────

export type GatewayClientOptions = {
  url: string;
  token?: string;
  password?: string;
  /**
   * WebSocket subprotocols offered to the server in the upgrade
   * request (`Sec-WebSocket-Protocol` header). Used in production for
   * the lexserver runtime-WS proxy: the SPA opens
   *   new WebSocket(url, [`lexiun-jwt.${token}`])
   * so the proxy can verify the JWT and resolve the per-user runtime
   * BEFORE the upgrade is accepted. Local dev leaves this unset.
   * Mirrors lexide/src/lib/gateway-client.ts.
   */
  subprotocols?: readonly string[];
  /**
   * Re-fetch auth before each reconnect. Without this hook a tab left
   * open overnight reconnects forever with a stale JWT after the ~1h
   * TTL and never recovers until the user manually reloads.
   * Mirrors the same option in lexide/src/lib/gateway-client.ts —
   * see that file's commentary for the full rationale.
   */
  refreshAuth?: () => Promise<{ token: string; subprotocols?: readonly string[] }>;
  onStateChange?: (state: GatewayConnectionState) => void;
  onEvent?: (evt: GatewayEventFrame) => void;
  onHello?: (hello: GatewayHelloOk) => void;
};

// ── Client ──────────────────────────────────────────────────────

const CONNECT_FAILED_CODE = 4008;
const REQUEST_TIMEOUT_MS = 30_000;

type Pending = {
  resolve: (value: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

export class GatewayClient {
  private ws: WebSocket | null = null;
  private pending = new Map<string, Pending>();
  private closed = false;
  private lastSeq: number | null = null;
  private connectNonce: string | null = null;
  private connectSent = false;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  // Pending backoff-scheduled reconnect. Tracked so `forceReconnect`
  // can cancel it and avoid a double-fire (one from the backoff timer,
  // one from the force path). Without this, both paths would race —
  // two refreshAuth HTTP calls and two `new WebSocket(...)` constructions.
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private backoffMs = 1000;
  private _state: GatewayConnectionState = "disconnected";

  constructor(private opts: GatewayClientOptions) {}

  get state(): GatewayConnectionState {
    return this._state;
  }

  start(): void {
    this.closed = false;
    this.connect();
  }

  stop(): void {
    this.closed = true;
    if (this.connectTimer !== null) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
    this.flushPending(new Error("client stopped"));
    this.setState("disconnected");
  }

  /** Update auth token (e.g. after Clerk exchange refresh). */
  updateToken(token: string): void {
    this.opts.token = token;
  }

  async request<T = unknown>(method: string, params?: unknown): Promise<T> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error("gateway not connected");
    }
    const id = crypto.randomUUID();
    const frame = { type: "req", id, method, params };

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`request "${method}" timed out after ${REQUEST_TIMEOUT_MS}ms`));
      }, REQUEST_TIMEOUT_MS);

      this.pending.set(id, {
        resolve: (v) => resolve(v as T),
        reject,
        timer,
      });
      this.ws!.send(JSON.stringify(frame));
    });
  }

  // ── Internal ────────────────────────────────────────────────

  private setState(s: GatewayConnectionState): void {
    if (this._state === s) return;
    this._state = s;
    this.opts.onStateChange?.(s);
  }

  private connect(): void {
    if (this.closed) return;
    this.setState("connecting");
    // Pass subprotocols only when set — local dev keeps proxy-direct,
    // production hands the JWT to the lexserver WS proxy.
    const subs = this.opts.subprotocols;
    this.ws = subs && subs.length > 0
      ? new WebSocket(this.opts.url, subs as string[])
      : new WebSocket(this.opts.url);

    this.ws.onopen = () => this.queueConnect();

    this.ws.onmessage = (ev) => this.handleMessage(String(ev.data ?? ""));

    this.ws.onclose = (ev) => {
      this.ws = null;
      this.flushPending(new Error(`closed (${ev.code}): ${ev.reason ?? ""}`));
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      // close handler will fire
    };
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    this.setState("disconnected");
    const delay = this.backoffMs;
    this.backoffMs = Math.min(this.backoffMs * 2, 30_000);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.reconnectWithRefresh();
    }, delay);
  }

  /** Force an immediate fresh-auth reconnect — called by the
   *  visibilitychange listener when the tab returns from a long
   *  hidden state. Bypasses the up-to-30s backoff. Cancels any
   *  pending scheduled reconnect so the backoff timer doesn't also
   *  fire alongside this one.
   *
   *  Note: state set to "connecting" (not "reconnecting") because
   *  lexportal's GatewayConnectionState enum does not have a
   *  "reconnecting" variant — same state used by `connect()`. */
  forceReconnect(): void {
    if (this.closed) return;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(1000, "force reconnect"); } catch { /* noop */ }
      this.ws = null;
    }
    this.backoffMs = 1000;
    this.setState("connecting");
    void this.reconnectWithRefresh();
  }

  private async reconnectWithRefresh(): Promise<void> {
    if (this.closed) return;
    if (this.opts.refreshAuth) {
      try {
        const refreshed = await this.opts.refreshAuth();
        this.opts.token = refreshed.token;
        if (refreshed.subprotocols !== undefined) {
          this.opts.subprotocols = refreshed.subprotocols;
        }
      } catch {
        // Best-effort: proceed with cached creds. Next reconnect retries.
      }
    }
    if (this.closed) return;
    this.connect();
  }

  private flushPending(err: Error): void {
    for (const [, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(err);
    }
    this.pending.clear();
  }

  private queueConnect(): void {
    this.connectNonce = null;
    this.connectSent = false;
    if (this.connectTimer !== null) clearTimeout(this.connectTimer);
    this.connectTimer = setTimeout(() => {
      void this.sendConnect();
    }, 100);
  }

  private async sendConnect(): Promise<void> {
    if (this.connectSent) return;
    this.connectSent = true;
    if (this.connectTimer !== null) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }

    const auth =
      this.opts.token || this.opts.password
        ? { token: this.opts.token, password: this.opts.password }
        : undefined;

    const params: Record<string, unknown> = {
      minProtocol: 3,
      maxProtocol: 3,
      client: {
        id: "openclaw-control-ui",
        version: "0.1.0",
        platform: navigator?.platform ?? "web",
        mode: "ui",
      },
      role: "operator",
      scopes: ["operator.admin"],
      caps: [],
      auth,
      userAgent: navigator?.userAgent,
      locale: navigator?.language,
    };

    try {
      const hello = await this.request<GatewayHelloOk>("connect", params);
      this.backoffMs = 1000;
      this.setState("connected");
      this.opts.onHello?.(hello);
    } catch {
      this.ws?.close(CONNECT_FAILED_CODE, "connect failed");
    }
  }

  private handleMessage(raw: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }

    const frame = parsed as { type?: string };

    // Event frames
    if (frame.type === "event") {
      const evt = parsed as GatewayEventFrame;

      // Challenge-response: server sends nonce before connect
      if (evt.event === "connect.challenge") {
        const payload = evt.payload as { nonce?: string } | undefined;
        if (payload?.nonce) {
          this.connectNonce = payload.nonce;
          void this.sendConnect();
        }
        return;
      }

      // Sequence gap detection
      const seq = typeof evt.seq === "number" ? evt.seq : null;
      if (seq !== null && this.lastSeq !== null && seq > this.lastSeq + 1) {
        console.warn(`[gateway] sequence gap: expected ${this.lastSeq + 1}, got ${seq}`);
      }
      if (seq !== null) this.lastSeq = seq;

      this.opts.onEvent?.(evt);
      return;
    }

    // Response frames
    if (frame.type === "res") {
      const res = parsed as GatewayResponseFrame;
      const pending = this.pending.get(res.id);
      if (!pending) return;
      this.pending.delete(res.id);
      clearTimeout(pending.timer);
      if (res.ok) pending.resolve(res.payload);
      else pending.reject(new Error(res.error?.message ?? "request failed"));
    }
  }
}
